import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScreenRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [shareableLink, setShareableLink] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState("");
  const [browserSupport, setBrowserSupport] = useState(null);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState('default');
  const [showMicrophoneModal, setShowMicrophoneModal] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const screenStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const combinedStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  
  const recordingTimeLimit = 120; // 2 minutes in seconds

  // Check browser support and enumerate microphones on component mount
  useEffect(() => {
    const checkSupport = async () => {
      const support = {
        mediaDevices: !!navigator.mediaDevices,
        getDisplayMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia),
        getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        mediaRecorder: !!window.MediaRecorder,
        https: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
        supportedMimeTypes: []
      };
      
      if (support.mediaRecorder) {
        const mimeTypes = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp9,opus',
          'video/webm',
          'video/mp4'
        ];
        
        support.supportedMimeTypes = mimeTypes.filter(type => 
          MediaRecorder.isTypeSupported(type)
        );
      }
      
      support.isSupported = support.mediaDevices && support.getDisplayMedia && 
                           support.getUserMedia && support.mediaRecorder && 
                           support.https && support.supportedMimeTypes.length > 0;
      
      setBrowserSupport(support);
      
      // Enumerate microphones if supported
      if (support.mediaDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const microphones = devices.filter(device => device.kind === 'audioinput');
          setAvailableMicrophones(microphones);
          
          // Set default microphone if available
          if (microphones.length > 0) {
            setSelectedMicrophone(microphones[0].deviceId || 'default');
          }
        } catch (err) {
          console.error('Error enumerating microphones:', err);
        }
      }
    };
    
    checkSupport();
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start timer
  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        // Auto-stop at 2 minutes
        if (newTime >= recordingTimeLimit) {
          stopRecording();
          return recordingTimeLimit;
        }
        return newTime;
      });
    }, 1000);
  };

  // Stop timer
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Start screen recording
  const startRecording = async () => {
    try {
      setError("");
      setShareableLink("");
      setRecordedBlob(null);
      setUploadProgress(0);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        setError('Screen sharing is not supported in this browser. Please try Chrome, Firefox, or Edge.');
        return;
      }
      
      if (!window.MediaRecorder) {
        setError('MediaRecorder API not supported in this browser.');
        return;
      }
      
      // Get microphone stream FIRST - REQUIRED
      let micStream = null;
      try {
        console.log('Requesting microphone permission...');
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: selectedMicrophone === 'default' ? undefined : { exact: selectedMicrophone },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        micStreamRef.current = micStream;
        console.log('Microphone access granted');
      } catch (micError) {
        console.error('Microphone access denied or failed:', micError);
        // Show modal for microphone permission
        setShowMicrophoneModal(true);
        return; // Don't continue without microphone
      }

      console.log('Requesting screen sharing permission...');
      
      // Get screen stream with optimized settings
      let screenStream = null;
      try {
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 15, max: 30 } // Lower frame rate for smaller files
          },
          audio: true // This captures system audio
        });

        screenStreamRef.current = screenStream;
      } catch (screenError) {
        console.error('Screen sharing denied or failed:', screenError);
        // Stop microphone stream if screen sharing fails
        if (micStream) {
          micStream.getTracks().forEach(track => track.stop());
        }
        
        if (screenError.name === 'NotAllowedError') {
          setError('Screen recording permission was denied. Please allow screen sharing and try again.');
        } else if (screenError.name === 'NotSupportedError') {
          setError('Screen sharing is not supported in this browser. Please try Chrome, Firefox, or Edge.');
        } else if (screenError.name === 'AbortError') {
          setError('Screen sharing was cancelled. Please try again.');
        } else {
          setError(`Failed to start screen recording: ${screenError.message}`);
        }
        return;
      }
      
      // Combine streams - microphone is required
      let finalStream;
      
      // Check if we have audio from screen
      const hasScreenAudio = screenStream.getAudioTracks().length > 0;
      const hasMicAudio = micStream.getAudioTracks().length > 0;
      
      console.log(`Screen audio tracks: ${screenStream.getAudioTracks().length}`);
      console.log(`Microphone audio tracks: ${micStream.getAudioTracks().length}`);
      
      if (hasScreenAudio && hasMicAudio) {
        // Mix both audio sources using Web Audio API
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          
          // Create source nodes
          const screenAudioSource = audioContext.createMediaStreamSource(screenStream);
          const micAudioSource = audioContext.createMediaStreamSource(micStream);
          
          // Create a gain node to control audio levels
          const screenGain = audioContext.createGain();
          const micGain = audioContext.createGain();
          
          // Set gain levels (adjust screen audio to be less dominant)
          screenGain.gain.value = 0.7; // Reduce screen audio slightly
          micGain.gain.value = 1.0;    // Keep mic at full volume
          
          // Create destination for mixed audio
          const destination = audioContext.createMediaStreamDestination();
          
          // Connect sources through gains to destination
          screenAudioSource.connect(screenGain);
          micAudioSource.connect(micGain);
          screenGain.connect(destination);
          micGain.connect(destination);
          
          // Create final stream with video from screen and mixed audio
          finalStream = new MediaStream([
            ...screenStream.getVideoTracks(),
            ...destination.stream.getAudioTracks()
          ]);
          
          console.log('Mixed screen and microphone audio using Web Audio API');
        } catch (audioError) {
          console.warn('Web Audio API mixing failed, falling back to microphone only:', audioError);
          // Fallback: use microphone audio only
          finalStream = new MediaStream([
            ...screenStream.getVideoTracks(),
            ...micStream.getAudioTracks()
          ]);
          console.log('Using microphone audio only as fallback');
        }
      } else if (hasMicAudio) {
        // Only microphone audio available
        finalStream = new MediaStream([
          ...screenStream.getVideoTracks(),
          ...micStream.getAudioTracks()
        ]);
        console.log('Using microphone audio only');
      } else if (hasScreenAudio) {
        // Only screen audio available (shouldn't happen since mic is required)
        finalStream = new MediaStream([
          ...screenStream.getVideoTracks(),
          ...screenStream.getAudioTracks()
        ]);
        console.log('Using screen audio only');
      } else {
        // No audio available - this shouldn't happen but handle gracefully
        finalStream = new MediaStream([
          ...screenStream.getVideoTracks()
        ]);
        console.warn('No audio tracks available');
        setError('Warning: No audio detected. Recording will proceed without audio.');
      }
      
      combinedStreamRef.current = finalStream;
      chunksRef.current = [];
      
      console.log('Stream obtained, setting up MediaRecorder...');
      
      // Debug: Log final stream details
      console.log(`Final stream - Video tracks: ${finalStream.getVideoTracks().length}`);
      console.log(`Final stream - Audio tracks: ${finalStream.getAudioTracks().length}`);
      
      finalStream.getAudioTracks().forEach((track, index) => {
        console.log(`Audio track ${index}:`, {
          id: track.id,
          kind: track.kind,
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState
        });
      });

      // Optimized MediaRecorder settings for smaller file sizes
      const options = {
        mimeType: 'video/webm;codecs=vp8,opus',
        videoBitsPerSecond: 1000000, // 1 Mbps (reduced from default)
        audioBitsPerSecond: 128000    // Increased from 64k to 128k for better audio quality
      };
      
      // Fallback options if above not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn('Preferred MIME type not supported, trying fallbacks...');
        const fallbackTypes = [
          'video/webm;codecs=vp9,opus',
          'video/webm',
          'video/mp4'
        ];
        
        for (const type of fallbackTypes) {
          if (MediaRecorder.isTypeSupported(type)) {
            options.mimeType = type;
            break;
          }
        }
        
        // Remove bitrate settings for fallback
        delete options.videoBitsPerSecond;
        delete options.audioBitsPerSecond;
      }
      
      console.log('Using MIME type:', options.mimeType);
      console.log('Video bitrate:', options.videoBitsPerSecond || 'default');
      console.log('Audio bitrate:', options.audioBitsPerSecond || 'default');
      
      const mediaRecorder = new MediaRecorder(finalStream, options);
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        console.log('Recording stopped, creating blob...');
        const blob = new Blob(chunksRef.current, { type: options.mimeType.split(';')[0] });
        console.log('Blob created:', blob.size, 'bytes');
        setRecordedBlob(blob);
        stopTimer();
        
        // Stop all tracks
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (micStreamRef.current) {
          micStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (combinedStreamRef.current) {
          combinedStreamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // Auto-upload
        uploadRecording(blob);
      };
      
      // Handle recording errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError(`Recording error: ${event.error.message}`);
        setIsRecording(false);
        stopTimer();
      };

      // Handle stream end (user stops sharing)
      const videoTrack = screenStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          console.log('Video track ended');
          if (isRecording) {
            stopRecording();
          }
        };
      }

      // Start recording
      console.log('Starting MediaRecorder...');
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      startTimer();
      
      console.log('Recording started successfully');

    } catch (err) {
      console.error('Error starting recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Screen recording permission was denied. Please allow screen sharing and try again.');
      } else if (err.name === 'NotSupportedError') {
        setError('Screen sharing is not supported in this browser. Please try Chrome, Firefox, or Edge.');
      } else if (err.name === 'AbortError') {
        setError('Screen sharing was cancelled. Please try again.');
      } else {
        setError(`Failed to start recording: ${err.message}`);
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    stopTimer();
  };

  // Upload recording with the blob parameter
  const uploadRecording = async (blob = recordedBlob) => {
    if (!blob) return;

    try {
      setIsUploading(true);
      setError("");

      const fileSize = blob.size;
      const fileSizeMB = fileSize / (1024 * 1024);
      // Use a larger threshold so small recordings go through a single request
      // (fewer TLS handshakes => avoids transient ERR_SSL_BAD_RECORD_MAC_ALERT).
      const directUploadLimit = 8 * 1024 * 1024; // 8MB

      console.log(`File size: ${fileSizeMB.toFixed(2)} MB (${fileSize} bytes)`);
      console.log(`Direct upload limit: ${directUploadLimit / (1024 * 1024)} MB`);

      if (fileSize > directUploadLimit) {
        console.log('Using chunked upload for large file:', fileSize, 'bytes');
        await uploadWithChunks(blob);
      } else {
        console.log('Using direct upload for small file:', fileSize, 'bytes');
        await uploadDirect(blob);
      }

    } catch (err) {
      console.error('Error uploading recording:', err);
      setError('Failed to upload recording: ' + err.message);
      setIsUploading(false);
    }
  };

  // Direct upload for smaller files
  const uploadDirect = async (blob) => {
    const formData = new FormData();
    formData.append('video', blob, `recording-${Date.now()}.webm`);

    // Use XMLHttpRequest for progress tracking
    const xhr = new XMLHttpRequest();
    
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percentComplete);
      }
    };
    
    xhr.onload = () => {
      setIsUploading(false);
      
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        setShareableLink(response.shareable_url);
      } else if (xhr.status === 413) {
        setError('Recording is too large. Please try a shorter recording (under 2 minutes) or contact support if the issue persists.');
      } else {
        const error = JSON.parse(xhr.responseText);
        setError('Upload failed: ' + (error.detail || 'Unknown error'));
      }
    };
    
    xhr.onerror = () => {
      setIsUploading(false);
      setError('Upload failed: Network error');
    };
    
    xhr.open('POST', `${API}/recordings/upload`);
    xhr.send(formData);
  };

  // Chunked upload for larger files
  const uploadWithChunks = async (blob) => {
    const chunkSize = 2 * 1024 * 1024; // 2MB chunks (reduces TLS handshakes)
    const totalChunks = Math.ceil(blob.size / chunkSize);
    const uploadId = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);

    console.log(`Uploading in ${totalChunks} chunks of ${chunkSize / (1024 * 1024)} MB each`);

    // Helper: upload a single chunk with retry on transient network/TLS errors
    const uploadChunkWithRetry = async (chunk, index, maxRetries = 4) => {
      let lastError;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const formData = new FormData();
          formData.append('chunk', chunk);
          formData.append('chunk_index', index.toString());
          formData.append('total_chunks', totalChunks.toString());
          formData.append('upload_id', uploadId);

          const response = await fetch(`${API}/recordings/upload-chunk`, {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status} ${response.statusText} - ${errorText}`);
          }
          return;
        } catch (err) {
          lastError = err;
          console.warn(`Chunk ${index} attempt ${attempt}/${maxRetries} failed: ${err.message}`);
          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = 1000 * Math.pow(2, attempt - 1);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }
      throw lastError;
    };

    // Upload each chunk sequentially with retry
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, blob.size);
      const chunk = blob.slice(start, end);

      console.log(`Uploading chunk ${i + 1}/${totalChunks} (${chunk.size} bytes)`);

      try {
        await uploadChunkWithRetry(chunk, i);
        const progress = Math.round(((i + 1) / totalChunks) * 90); // 90% for uploading, 10% for finalization
        setUploadProgress(progress);
        console.log(`Chunk ${i + 1} uploaded successfully, progress: ${progress}%`);
      } catch (error) {
        console.error(`Chunk ${i} upload error after retries:`, error);
        throw new Error(`Failed to upload chunk ${i} after retries: ${error.message}`);
      }
    }
    
    // Finalize upload (with retry on transient errors)
    console.log('Finalizing upload...');
    setUploadProgress(95);

    const finalizeWithRetry = async (maxRetries = 4) => {
      let lastError;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const finalizeFormData = new FormData();
          finalizeFormData.append('upload_id', uploadId);
          finalizeFormData.append('total_chunks', totalChunks.toString());
          finalizeFormData.append('filename', `recording-${Date.now()}.webm`);

          const finalizeResponse = await fetch(`${API}/recordings/finalize-upload`, {
            method: 'POST',
            body: finalizeFormData
          });

          if (!finalizeResponse.ok) {
            const errorText = await finalizeResponse.text();
            throw new Error(`HTTP ${finalizeResponse.status} ${finalizeResponse.statusText} - ${errorText}`);
          }
          return await finalizeResponse.json();
        } catch (err) {
          lastError = err;
          console.warn(`Finalize attempt ${attempt}/${maxRetries} failed: ${err.message}`);
          if (attempt < maxRetries) {
            const delay = 1000 * Math.pow(2, attempt - 1);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }
      throw lastError;
    };

    const result = await finalizeWithRetry();
    console.log('Upload finalized successfully:', result);

    setIsUploading(false);
    setUploadProgress(100);
    setShareableLink(result.shareable_url);
  };

  // Download recording locally
  const downloadRecording = () => {
    if (!recordedBlob) return;

    const url = URL.createObjectURL(recordedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `screen-recording-${new Date().toISOString().slice(0, 19)}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Start over
  const startOver = () => {
    setRecordedBlob(null);
    setShareableLink("");
    setError("");
    setRecordingTime(0);
    setUploadProgress(0);
  };

  // Copy link to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Open shareable link
  const openShareableLink = () => {
    window.open(shareableLink, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/logo.png"
              alt="The Follow Up Ninja Logo" 
              className="w-16 h-16 mr-4"
            />
            <div className="text-left">
              <h1 className="text-4xl font-bold text-gray-800">
                🎥 TFUN Screen Recorder
              </h1>
              <p className="text-gray-600 text-lg">
                Easy & Free - Record your screen to help our support team assist you better
              </p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 font-medium">
              ⏰ <strong>2-minute recording limit</strong> - Focus on the key issue for the most effective support!
            </p>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          
          {/* Browser Compatibility Check */}
          {browserSupport && !browserSupport.isSupported && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-lg font-semibold text-red-800 mb-2">
                ⚠️ Browser Compatibility Issues
              </h4>
              <div className="text-red-700 text-sm space-y-1">
                {!browserSupport.https && <div>• HTTPS required for screen recording</div>}
                {!browserSupport.mediaDevices && <div>• MediaDevices API not supported</div>}
                {!browserSupport.getDisplayMedia && <div>• Screen sharing not supported</div>}
                {!browserSupport.mediaRecorder && <div>• MediaRecorder API not supported</div>}
                {browserSupport.supportedMimeTypes.length === 0 && <div>• No supported video formats</div>}
              </div>
              <p className="text-red-700 text-sm mt-2">
                Please try using Chrome, Firefox, or Edge with HTTPS enabled.
              </p>
            </div>
          )}

          {/* Microphone Settings */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-800 mb-3">
              🎤 Audio Settings
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-600 rounded mr-2"></div>
                <label className="text-blue-700">
                  <strong>Microphone audio is required</strong> for all recordings
                </label>
              </div>
              
              {/* Microphone Selection */}
              {availableMicrophones.length > 0 && (
                <div>
                  <label htmlFor="microphoneSelect" className="block text-sm font-medium text-blue-700 mb-2">
                    Select Microphone:
                  </label>
                  <select
                    id="microphoneSelect"
                    value={selectedMicrophone}
                    onChange={(e) => setSelectedMicrophone(e.target.value)}
                    className="w-full p-2 border border-blue-300 rounded-lg bg-white text-blue-800"
                  >
                    <option value="default">Default Microphone</option>
                    {availableMicrophones.map((mic, index) => (
                      <option key={mic.deviceId || index} value={mic.deviceId || 'default'}>
                        {mic.label || `Microphone ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {availableMicrophones.length === 0 && (
                <div className="text-sm text-blue-600">
                  No microphones detected. Make sure you have a microphone connected.
                </div>
              )}
              
              <div className="text-sm text-blue-600">
                <strong>Note:</strong> Both microphone and screen audio will be recorded together for the best support experience.
              </div>
            </div>
          </div>

          {/* Recording Controls */}
          <div className="text-center mb-8">
            {!isRecording && !recordedBlob && (
              <button
                onClick={startRecording}
                disabled={!browserSupport?.isSupported}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-4 px-8 rounded-lg text-xl transition-colors"
              >
                📹 Start Screen Recording
              </button>
            )}

            {isRecording && (
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-600 font-semibold">Recording...</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {formatTime(recordingTime)}
                  </div>
                </div>
                
                {recordingTime >= 60 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-yellow-800 font-medium">
                      ⏰ <strong>1 minute remaining!</strong> Recording will auto-stop soon.
                    </p>
                  </div>
                )}
                
                <button
                  onClick={stopRecording}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
                >
                  ⏹️ Stop Recording
                </button>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-lg font-semibold text-blue-800 mb-3">
                📤 Uploading Recording...
              </h4>
              <div className="w-full bg-blue-200 rounded-full h-4">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{ width: `${uploadProgress}%` }}
                >
                  <span className="text-white text-xs font-bold">
                    {uploadProgress}%
                  </span>
                </div>
              </div>
              <p className="text-blue-700 text-sm mt-2">
                Please wait while your recording is being uploaded...
              </p>
            </div>
          )}

          {/* Success Result */}
          {shareableLink && !isUploading && (
            <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-lg font-semibold text-green-800 mb-3">
                ✅ Recording Uploaded Successfully!
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-2">
                    Shareable Link:
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={shareableLink}
                      readOnly
                      className="flex-1 p-3 border border-green-300 rounded-lg bg-white text-sm font-mono"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={openShareableLink}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                  >
                    🔗 Open Recording
                  </button>
                  {recordedBlob && (
                    <button
                      onClick={downloadRecording}
                      className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                    >
                      ⬇️ Download
                    </button>
                  )}
                  <button
                    onClick={startOver}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                  >
                    🔄 Record Another
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-lg font-semibold text-red-800 mb-2">❌ Error</h4>
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">
              📋 How to Use
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Click &quot;Start Screen Recording&quot; button</li>
              <li>Choose what to share (entire screen, window, or browser tab)</li>
              <li>Click &quot;Share&quot; in the browser popup</li>
              <li>Record your issue (max 2 minutes)</li>
              <li>Click &quot;Stop Recording&quot; when done</li>
              <li>Your recording will automatically upload</li>
              <li>Copy the shareable link and send it to support</li>
            </ol>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>💡 Tips:</strong> For best results, close unnecessary browser tabs and applications 
                before recording. Focus on the specific issue you&apos;re experiencing.
              </p>
            </div>
          </div>
        </div>
        
        {/* Microphone Permission Modal */}
        {showMicrophoneModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all">
              <div className="p-6 text-center">
                {/* Icon */}
                <div className="mx-auto flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                  </svg>
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Audio is required!
                </h3>
                
                {/* Message */}
                <p className="text-gray-600 mb-6">
                  Please give permission to record sound. Audio helps our support team understand your issue better.
                </p>
                
                {/* Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowMicrophoneModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowMicrophoneModal(false);
                      // Try recording again
                      setTimeout(() => startRecording(), 100);
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <ScreenRecorder />
    </div>
  );
}

export default App;
